import { Navigate } from 'react-router-dom'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { getDefaultDashboardRoute } from '@/lib/utils/get-default-dashboard-route'
import type { FeatureFlag } from '@/types/feature-flags'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireSuperAdmin?: boolean
  enabledForFeatureFlag?: FeatureFlag
}

export function ProtectedRoute({
  children,
  requireSuperAdmin = false,
  enabledForFeatureFlag,
}: ProtectedRouteProps) {
  const { user, isAuthLoading, isSuperAdmin } = useAppAuth()
  const { isEnabled, isLoading: isFlagsLoading } = useFeatureFlag()

  // If we know for certain there's no user and auth check is complete, redirect
  if (!isAuthLoading && !user) {
    return <Navigate to="/sign-in" replace />
  }

  // If super admin is required and user is not a super admin, redirect to default dashboard
  if (!isAuthLoading && user && requireSuperAdmin && !isSuperAdmin) {
    const defaultRoute = getDefaultDashboardRoute(true)
    return <Navigate to={defaultRoute} replace />
  }

  // Check feature flag if required
  if (
    enabledForFeatureFlag &&
    !isFlagsLoading &&
    !isEnabled(enabledForFeatureFlag)
  ) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <h1 className="text-2xl font-semibold mb-2">Feature Not Available</h1>
        <p className="text-muted-foreground mb-4">
          This feature is not enabled for your organization.
        </p>
        <p className="text-sm text-muted-foreground">
          Please contact your administrator to enable this feature.
        </p>
      </div>
    )
  }

  // Show the UI (potentially in skeleton state) while checking auth or initializing
  return <>{children}</>
}
